import {Trans} from '@lingui/macro';
import {Box, Divider, makeStyles, Dialog, IconButton} from '@material-ui/core';
import {Edit, PostAdd, Visibility} from '@material-ui/icons';
import {Column, Lookup} from 'devextreme-react/data-grid';
import {
  ContentTypes,
  LinkDisplayer,
} from 'features/capacitation/component/Online';
import PDFDisplayer from 'features/capacitation/component/PDFDisplayer';
import {ChoicePage} from 'features/choice';
import CreateContentPopup from 'features/edit-course/components/CreateContentPopup';
import {i18n2} from 'features/i18n/i18n';
import React, {useState} from 'react';
import {IntegrityGrid} from '../../../components';
import {useUpdateContent} from '../hooks';
import useDeleteContent from '../hooks/useDeleteContent';
import useLoadContent from '../hooks/useLoadContent';
import IntegrityContentGrid from './IntegrityContentGrid';
import MediaDisplayer from './../../capacitation/component/MediaDisplayer';
import {useIsTablet} from 'components/hooks/useIsTablet';
import {
  useActionButtonStyles,
  getActionColumnWidth,
} from 'components/grid-dx/styles/actionButtonStyles';

export const lookupType = {
  store: {
    type: 'array',
    data: [
      {id: ContentTypes.Link, name: i18n2._('Link')},
      {id: ContentTypes.PDF, name: i18n2._('PDF')},
      {id: ContentTypes.Choice, name: i18n2._('Choice')},
      {id: 3, name: i18n2._('Media')},
    ],
    key: 'id',
  },
};

const SeeLink = ({row}) => {
  const isTablet = useIsTablet();
  const styles = useActionButtonStyles();
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton
        size="small"
        title={i18n2._('Ver Link')}
        onClick={() => setOpen(true)}
        className={isTablet ? styles.tabletButton : styles.compactButton}>
        <Visibility
          className={isTablet ? styles.tabletIcon : styles.compactButton}
        />
      </IconButton>
      <Dialog
        open={open}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            width: '100%',
            overflowWrap: 'break-word',
            overflow: 'auto',
          },
        }}
        aria-describedby="alert-dialog-description"
        onClose={() => setOpen(false)}>
        <LinkDisplayer content={row} />
      </Dialog>
    </>
  );
};

const SeePdf = ({row}) => {
  const isTablet = useIsTablet();
  const styles = useActionButtonStyles();
  const [open, setOpen] = useState(false);
  const pdfHeight = isTablet ? '80vh' : '85vh';
  return (
    <>
      <IconButton
        size="small"
        title={i18n2._('Ver Pdf')}
        onClick={() => setOpen(true)}
        className={isTablet ? styles.tabletButton : styles.compactButton}>
        <Visibility
          className={isTablet ? styles.tabletIcon : styles.compactButton}
        />
      </IconButton>
      <Dialog
        open={open}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          style: {
            width: isTablet ? '95vw' : '85vw',
            maxWidth: '1200px',
            height: 'auto',
            margin: '24px auto',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflowWrap: 'break-word',
            overflow: 'auto',
            borderRadius: 8,
          },
        }}
        aria-describedby="alert-dialog-description"
        onClose={() => setOpen(false)}>
        <div style={{height: pdfHeight}}>
          <PDFDisplayer file={getPDFQuery(row)} height={pdfHeight} />
        </div>
      </Dialog>
    </>
  );
};

const getPDFQuery = (row) => {
  const url = '/CourseManager/GetCoursePDF';
  return (
    url +
    `?ID_PDF=${row.pdFs[0].iD_PDF}&ID_Content=${row.iD_Content}&ID_Course=${row.iD_Course}`
  );
};

const SeeChoices = ({row, noEdit, iD_Course, setIsChoiceActive}) => {
  const isTablet = useIsTablet();
  const styles = useActionButtonStyles();
  const handleActiveChoice = () => {
    setIsChoiceActive({
      open: true,
      iD_Course: iD_Course,
      iD_Content: row.iD_Content,
    });
  };

  return (
    <>
      <IconButton
        onClick={handleActiveChoice}
        size="small"
        title={i18n2._('Ir a la tabla choice')}
        className={isTablet ? styles.tabletButton : styles.compactButton}>
        {noEdit ? (
          <Visibility
            className={isTablet ? styles.tabletIcon : styles.compactButton}
          />
        ) : (
          <PostAdd
            className={isTablet ? styles.tabletIcon : styles.compactButton}
          />
        )}
      </IconButton>
    </>
  );
};

const SeeMedia = ({row}) => {
  const isTablet = useIsTablet();
  const styles = useActionButtonStyles();
  const [open, setOpen] = useState(false);
  const url = `/CourseManager/GetCourseMedia?ID=${row.medias[0].iD_Media}&ID_Content=${row.iD_Content}&ID_Course=${row.iD_Course}`;

  return (
    <>
      <IconButton
        size="small"
        title={i18n2._('Ver Media')}
        onClick={() => setOpen(true)}
        className={isTablet ? styles.tabletButton : styles.compactButton}>
        <Visibility
          className={isTablet ? styles.tabletIcon : styles.compactButton}
        />
      </IconButton>
      <Dialog
        open={open}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            width: '100%',
            overflowWrap: 'break-word',
            overflow: 'auto',
          },
        }}
        aria-describedby="alert-dialog-description"
        onClose={() => setOpen(false)}>
        <MediaDisplayer file={url} route={row.medias[0].route} />
      </Dialog>
    </>
  );
};

const EditButton = ({row, refresh}) => {
  const isTablet = useIsTablet();
  const styles = useActionButtonStyles();
  const updateContent = useUpdateContent();
  const [openPopupContent, setOpenPopupContent] = useState(false);
  const handleClick = () => {
    setOpenPopupContent(true);
  };

  return (
    <>
      <IconButton
        size="small"
        title={i18n2._('Editar')}
        className={isTablet ? styles.tabletButton : styles.compactButton}
        onClick={handleClick}>
        <Edit className={isTablet ? styles.tabletIcon : styles.compactButton} />
      </IconButton>
      {openPopupContent && (
        <CreateContentPopup
          open={openPopupContent}
          handleClose={() => setOpenPopupContent(false)}
          row={row}
          remoteUpdate={updateContent}
          refresh={refresh}
        />
      )}
    </>
  );
};

const MAX_ACTION_BUTTONS = 2;

const Buttons = ({data, noEdit, iD_Course, setIsChoiceActive, refresh}) => {
  const isTablet = useIsTablet();
  const styles = useActionButtonStyles();

  return (
    <div
      className={isTablet ? styles.tabletContainer : styles.compactContainer}>
      {!noEdit && !data.deleted && <EditButton row={data} refresh={refresh} />}
      {!data.deleted && data.type === ContentTypes.Choice && (
        <SeeChoices
          noEdit={noEdit}
          row={data}
          iD_Course={iD_Course}
          setIsChoiceActive={setIsChoiceActive}
        />
      )}

      {data.type === ContentTypes.Link && <SeeLink row={data} />}
      {data.type === ContentTypes.Media && <SeeMedia row={data} />}
      {data.type === ContentTypes.PDF && <SeePdf row={data} />}
    </div>
  );
};

const CustomAddPopup = (open, setOpen, refresh) => {
  return (
    <CreateContentPopup
      open={open}
      setOpen={setOpen}
      handleClose={() => setOpen(false)}
      refresh={refresh}
    />
  );
};

const ContentGrid = ({id, noEdit}) => {
  const styles = useStyles();
  const isTablet = useIsTablet();
  const loadContent = useLoadContent(id);
  const deleteContent = useDeleteContent();
  const [isChoiceActive, setIsChoiceActive] = useState({
    open: false,
    iD_Course: id,
    iD_Content: 0,
  });

  return !isChoiceActive.open ? (
    <Box p={3}>
      <h2 className={styles.title}>
        <Trans>Materiales</Trans>
      </h2>
      <Divider variant="middle" style={{marginBottom: 10}} />

      <IntegrityGrid
        dataSource={loadContent}
        id={['iD_Content', 'iD_Course']}
        promiseDelete={!noEdit ? deleteContent : null}
        customEditPopup={true}
        componentAddPopup={CustomAddPopup}
        integrityGrid={IntegrityContentGrid}
        integrityId={['ID_Course', 'ID_Content']}
        integrityName={'Content'}
        allowActions={!noEdit}
        height={'60vh'}>
        <Column
          caption={i18n2._('#Contenido')}
          dataField="iD_Content"
          visible={false}
        />
        <Column caption={i18n2._('Tipo')} dataField="type" width="5%">
          <Lookup
            dataSource={lookupType}
            valueExpr="id"
            displayExpr={(item) => {
              return i18n2._(item ? item.name : '');
            }}
          />
        </Column>
        <Column caption={i18n2._('Título')} dataField="title" width="13%" />
        <Column
          caption={i18n2._('Descripción')}
          dataField="description"
          width="40%"
        />
        <Column caption={i18n2._('Revision')} dataField="revision" width="8%" />
        <Column caption={i18n2._('Motivo')} dataField="reason" width="8%" />
        <Column
          caption={i18n2._('Fecha de actualizacion')}
          dataField="updateDate"
          defaultSortOrder={'desc'}
          dataType="date"
          format="dd/MM/yyyy"
          calculateCellValue={(row) => new Date(row.updateDate)}
          width="8%"
        />
        <Column
          caption={i18n2._('Actualizado por')}
          dataField="updatedBy"
          width="7%"
        />
        <Column
          caption={i18n2._('Eliminado')}
          dataField="deleted"
          visible={false}
          width="5%"
        />
        <Column
          caption=""
          cellComponent={(e) => (
            <Buttons
              data={e.data.data}
              noEdit={noEdit}
              iD_Course={id}
              setIsChoiceActive={setIsChoiceActive}
              refresh={e.data.component.refresh}
            />
          )}
          width={getActionColumnWidth(MAX_ACTION_BUTTONS, isTablet)}
          fixed
          alignment="right"
        />
      </IntegrityGrid>
    </Box>
  ) : (
    <ChoicePage
      idCourse={isChoiceActive.iD_Course}
      idContent={isChoiceActive.iD_Content}
      read={noEdit}
      setIsChoiceActive={(x) => setIsChoiceActive(x)}
    />
  );
};

const useStyles = makeStyles(() => ({
  title: {
    textAlign: 'center',
    marginTop: 0,
    marginBottom: 0,
  },
}));

export default ContentGrid;
